import * as React from 'react';
import type { IColumn } from '@fluentui/react';

import { useFeatureChildren } from '../../../Hooks/useFeature';
import { maskValue } from './GatedField';

/**
 * Applies feature-tree access to a DetailsList column set, data-driven and reusable across every
 * grid (page or modal). Columns whose capability is hidden (accessLevel 0) are dropped; read-only
 * or sensitive columns get a masking `onRender`. `colMap` maps each column key to its capability
 * key under `tableKey` — the only per-table wiring needed.
 */
export function useGatedColumns<T>(
  tableKey: string,
  colMap: Record<string, string>,
  columns: IColumn[],
): IColumn[] {
  const children = useFeatureChildren(tableKey);

  return React.useMemo(() => {
    const access = new Map<string, number>();
    children.forEach((c) => access.set(c.key, c.node.isSensitive ? 1 : c.node.accessLevel));

    return columns.reduce<IColumn[]>((acc, col) => {
      const capKey = colMap[col.key];
      // Columns without a mapped capability are always shown (structural/non-sensitive).
      if (!capKey) {
        acc.push(col);
        return acc;
      }
      const level = access.get(capKey) ?? 0;
      if (level === 0) return acc; // hidden
      if (level === 1) {
        acc.push({
          ...col,
          onRender: (item: T) => <span>{maskValue((item as Record<string, unknown>)[col.fieldName as string])}</span>,
        });
        return acc;
      }
      acc.push(col); // level 2 — full
      return acc;
    }, []);
  }, [children, colMap, columns]);
}
